import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface Curso {
  id: number;
  titulo: string;
  descricao: string | null;
  categoria: string;
  nivel: string;
  preco: number | null;
  imagem_url: string | null;
  status: string;
  created_at: string;
}

interface Aula {
  id: number;
  curso_id: number;
  titulo: string;
  descricao: string | null;
  tipo: "pdf" | "video";
  arquivo_url: string;
  ordem: number;
  duracao_segundos: number | null;
}

const CATEGORIAS = ["Leitura e Teoria Musical", "Harmonia Musical", "Produção Musical", "Outros"];
const NIVEIS = ["Iniciante", "Intermediário", "Avançado", "Todos os Níveis"];

export default function CursoManager() {
  const [cursos, setCursos] = useState<Curso[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");

  // Modal curso
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Curso | null>(null);
  const [form, setForm] = useState({
    titulo: "",
    descricao: "",
    categoria: "Leitura e Teoria Musical",
    nivel: "Iniciante",
    preco: "",
    imagem_url: "",
    status: "draft",
  });

  // Gerenciar aulas de um curso
  const [aulasCursoId, setAulasCursoId] = useState<number | null>(null);
  const [aulasCursoNome, setAulasCursoNome] = useState("");
  const [aulas, setAulas] = useState<Aula[]>([]);
  const [loadingAulas, setLoadingAulas] = useState(false);

  // Upload de aula
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTipo, setUploadTipo] = useState<"pdf" | "video">("pdf");
  const [uploadTitulo, setUploadTitulo] = useState("");
  const [uploadDescricao, setUploadDescricao] = useState("");
  const [uploadDuracao, setUploadDuracao] = useState("");
  const [uploading, setUploading] = useState(false);
  const [savingAula, setSavingAula] = useState(false);
  const [uploadStep, setUploadStep] = useState<"idle" | "info">("idle");
  const [uploadedUrl, setUploadedUrl] = useState("");

  const loadCursos = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("cursos").select("*").order("id", { ascending: false });
    if (data) setCursos(data as Curso[]);
    setLoading(false);
  }, []);

  useEffect(() => { loadCursos(); }, [loadCursos]);

  async function loadAulas(cursoId: number) {
    setLoadingAulas(true);
    const { data } = await supabase.from("curso_aulas").select("*").eq("curso_id", cursoId).order("ordem");
    if (data) setAulas(data as Aula[]);
    setLoadingAulas(false);
  }

  function openCreate() {
    setEditing(null);
    setForm({ titulo: "", descricao: "", categoria: "Leitura e Teoria Musical", nivel: "Iniciante", preco: "", imagem_url: "", status: "draft" });
    setErro("");
    setModalOpen(true);
  }

  function openEdit(c: Curso) {
    setEditing(c);
    setForm({
      titulo: c.titulo,
      descricao: c.descricao || "",
      categoria: c.categoria,
      nivel: c.nivel,
      preco: c.preco != null ? String(c.preco) : "",
      imagem_url: c.imagem_url || "",
      status: c.status,
    });
    setErro("");
    setModalOpen(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setErro("");
    setSaving(true);
    try {
      const payload = {
        titulo: form.titulo.trim(),
        descricao: form.descricao.trim() || null,
        categoria: form.categoria,
        nivel: form.nivel,
        preco: form.preco ? Number(form.preco) : null,
        imagem_url: form.imagem_url.trim() || null,
        status: form.status,
        updated_at: new Date().toISOString(),
      };
      if (editing) {
        const { error } = await supabase.from("cursos").update(payload).eq("id", editing.id);
        if (error) throw error;
        setMensagem("Curso atualizado com sucesso!");
      } else {
        const { error } = await supabase.from("cursos").insert(payload);
        if (error) throw error;
        setMensagem("Curso criado com sucesso!");
      }
      setModalOpen(false);
      loadCursos();
    } catch (err: any) {
      setErro(err.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Excluir este curso? Todas as aulas vinculadas serão removidas.")) return;
    setLoading(true);
    const { error } = await supabase.from("cursos").delete().eq("id", id);
    if (error) setErro(error.message);
    else { setMensagem("Curso excluído."); loadCursos(); }
    setLoading(false);
  }

  function openGerenciarAulas(c: Curso) {
    setAulasCursoId(c.id);
    setAulasCursoNome(c.titulo);
    setUploadStep("idle");
    setUploadFile(null);
    setUploadTitulo("");
    setUploadDescricao("");
    setUploadDuracao("");
    setUploadedUrl("");
    setErro("");
    setMensagem("");
    loadAulas(c.id);
  }

  async function handleUploadArquivo(e: React.FormEvent) {
    e.preventDefault();
    if (!uploadFile || !aulasCursoId) return;
    setUploading(true);
    setErro("");
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("fileName", uploadFile.name);
      formData.append("cursoId", String(aulasCursoId));

      const funcName = uploadTipo === "pdf" ? "upload-partitura" : "upload-video";
      const { data, error } = await supabase.functions.invoke(funcName, { body: formData });
      if (error || !data) throw new Error(error?.message || "Erro no upload");
      if (data.error) throw new Error(data.error);

      setUploadedUrl(data.url);
      setUploadStep("info");
      setUploadFile(null);
    } catch (err: any) {
      setErro(err.message || "Erro ao enviar arquivo.");
    } finally {
      setUploading(false);
    }
  }

  async function handleSaveAula(e: React.FormEvent) {
    e.preventDefault();
    if (!aulasCursoId || !uploadedUrl) return;
    setSavingAula(true);
    setErro("");
    try {
      const proximaOrdem = aulas.length > 0 ? Math.max(...aulas.map((a) => a.ordem)) + 1 : 1;
      const { error } = await supabase.from("curso_aulas").insert({
        curso_id: aulasCursoId,
        titulo: uploadTitulo.trim(),
        descricao: uploadDescricao.trim() || null,
        tipo: uploadTipo,
        arquivo_url: uploadedUrl,
        ordem: proximaOrdem,
        duracao_segundos: uploadDuracao ? Number(uploadDuracao) * 60 : null,
      });
      if (error) throw new Error(error.message);
      setMensagem("Aula adicionada com sucesso!");
      setUploadStep("idle");
      setUploadedUrl("");
      setUploadTitulo("");
      setUploadDescricao("");
      setUploadDuracao("");
      setUploadFile(null);
      loadAulas(aulasCursoId);
    } catch (err: any) {
      setErro(err.message || "Erro ao salvar aula.");
    } finally {
      setSavingAula(false);
    }
  }

  async function handleDeleteAula(id: number) {
    if (!confirm("Remover esta aula?")) return;
    const { error } = await supabase.from("curso_aulas").delete().eq("id", id);
    if (error) setErro(error.message);
    else { setMensagem("Aula removida."); if (aulasCursoId) loadAulas(aulasCursoId); }
  }

  async function moverAula(aulaId: number, direcao: "up" | "down") {
    const idx = aulas.findIndex((a) => a.id === aulaId);
    if (idx < 0) return;
    const outro = direcao === "up" ? aulas[idx - 1] : aulas[idx + 1];
    if (!outro) return;
    const atual = aulas[idx];
    await Promise.all([
      supabase.from("curso_aulas").update({ ordem: outro.ordem }).eq("id", atual.id),
      supabase.from("curso_aulas").update({ ordem: atual.ordem }).eq("id", outro.id),
    ]);
    if (aulasCursoId) loadAulas(aulasCursoId);
  }

  function formatDuracao(segundos: number | null) {
    if (!segundos) return "—";
    const min = Math.floor(segundos / 60);
    const sec = segundos % 60;
    return `${min}:${String(sec).padStart(2, "0")}`;
  }

  const filtered = cursos.filter((c) =>
    c.titulo.toLowerCase().includes(search.toLowerCase()) ||
    (c.descricao || "").toLowerCase().includes(search.toLowerCase())
  );

  // Painel de aulas
  if (aulasCursoId !== null) {
    return (
      <div>
        <button
          onClick={() => { setAulasCursoId(null); setMensagem(""); setErro(""); }}
          className="flex items-center gap-2 text-godoy-gold hover:text-godoy-goldlight text-sm mb-6 transition-colors"
        >
          <i className="ri-arrow-left-line" /> Voltar para lista de cursos
        </button>

        <h3 className="font-serif text-lg font-semibold text-godoy-cream mb-1">{aulasCursoNome}</h3>
        <p className="text-godoy-lightgray text-xs mb-6">Gerencie as aulas deste curso — adicione PDFs e vídeos.</p>

        {mensagem && <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md text-emerald-400 text-sm">{mensagem}</div>}
        {erro && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm">{erro}</div>}

        {/* Upload de nova aula */}
        <div className="mb-8 p-5 bg-godoy-black/40 border border-godoy-gray/20 rounded-lg">
          <h4 className="text-godoy-gold font-semibold text-sm mb-4 flex items-center gap-2">
            <i className="ri-upload-cloud-2-line" /> Adicionar Nova Aula
          </h4>

          {uploadStep === "idle" ? (
            <form onSubmit={handleUploadArquivo} className="space-y-4">
              <div className="flex gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => setUploadTipo("pdf")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${uploadTipo === "pdf" ? "bg-godoy-gold/10 text-godoy-gold border border-godoy-gold/30" : "text-godoy-lightgray hover:text-godoy-cream border border-godoy-gray/20"}`}
                >
                  <i className="ri-file-pdf-line" /> PDF / Partitura
                </button>
                <button
                  type="button"
                  onClick={() => setUploadTipo("video")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${uploadTipo === "video" ? "bg-godoy-gold/10 text-godoy-gold border border-godoy-gold/30" : "text-godoy-lightgray hover:text-godoy-cream border border-godoy-gray/20"}`}
                >
                  <i className="ri-video-line" /> Vídeo
                </button>
              </div>

              <div>
                <label className="block text-xs text-godoy-lightgray mb-1.5">
                  {uploadTipo === "pdf" ? "Arquivo PDF" : "Arquivo de Vídeo (MP4, WebM, MOV)"}
                </label>
                <input
                  type="file"
                  accept={uploadTipo === "pdf" ? "application/pdf" : "video/mp4,video/webm,video/quicktime,video/x-msvideo"}
                  required
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-godoy-cream file:bg-godoy-gold file:text-godoy-black file:border-0 file:rounded-md file:px-3 file:py-1.5 file:text-xs file:mr-3 file:font-medium"
                />
                {uploadFile && <p className="text-godoy-gold text-xs mt-1"><i className="ri-check-line mr-1" />{uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)</p>}
              </div>

              <button
                type="submit"
                disabled={uploading || !uploadFile}
                className="px-5 py-2 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60"
              >
                {uploading ? "Enviando..." : `Enviar ${uploadTipo === "pdf" ? "PDF" : "Vídeo"}`}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSaveAula} className="space-y-4">
              <div className="p-3 bg-godoy-gold/5 border border-godoy-gold/20 rounded-md">
                <p className="text-godoy-gold text-sm font-medium">
                  <i className={`${uploadTipo === "pdf" ? "ri-file-pdf-line" : "ri-video-line"} mr-1`} />
                  Arquivo enviado com sucesso!
                </p>
                <p className="text-godoy-lightgray/70 text-xs mt-0.5">Preencha os dados da aula:</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs text-godoy-lightgray mb-1.5">Título da Aula *</label>
                  <input required value={uploadTitulo} onChange={(e) => setUploadTitulo(e.target.value)} placeholder="ex: Introdução à Leitura de Partituras" className="w-full px-3 py-2 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50" />
                </div>
                <div>
                  <label className="block text-xs text-godoy-lightgray mb-1.5">Descrição</label>
                  <input value={uploadDescricao} onChange={(e) => setUploadDescricao(e.target.value)} placeholder="Breve descrição desta aula" className="w-full px-3 py-2 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50" />
                </div>
                {uploadTipo === "video" && (
                  <div>
                    <label className="block text-xs text-godoy-lightgray mb-1.5">Duração (minutos)</label>
                    <input type="number" min="0" value={uploadDuracao} onChange={(e) => setUploadDuracao(e.target.value)} placeholder="ex: 15" className="w-full px-3 py-2 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50" />
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={savingAula} className="px-5 py-2 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60">
                  {savingAula ? "Salvando..." : "Salvar Aula"}
                </button>
                <button type="button" onClick={() => { setUploadStep("idle"); setUploadedUrl(""); setErro(""); }} className="px-5 py-2 bg-godoy-gray/30 text-godoy-cream rounded-md hover:bg-godoy-gray/50 transition-all text-sm">
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Lista de aulas */}
        <h4 className="text-godoy-lightgray text-sm font-medium mb-3">Aulas do curso ({aulas.length})</h4>
        {loadingAulas ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : aulas.length === 0 ? (
          <p className="text-godoy-lightgray/50 text-sm text-center py-6">Nenhuma aula cadastrada ainda.</p>
        ) : (
          <div className="space-y-2">
            {aulas.map((aula, idx) => (
              <div key={aula.id} className="flex items-center gap-3 p-3 bg-godoy-black/40 border border-godoy-gray/20 rounded-md">
                <span className="text-godoy-lightgray/50 text-xs w-6 text-center">{idx + 1}</span>
                <i className={`${aula.tipo === "pdf" ? "ri-file-pdf-line text-red-400" : "ri-video-line text-blue-400"} text-lg flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="text-godoy-cream text-sm font-medium truncate">{aula.titulo}</p>
                  {aula.descricao && <p className="text-godoy-lightgray/60 text-xs truncate">{aula.descricao}</p>}
                </div>
                {aula.tipo === "video" && (
                  <span className="text-xs text-godoy-lightgray/50 flex-shrink-0">{formatDuracao(aula.duracao_segundos)}</span>
                )}
                <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${aula.tipo === "pdf" ? "bg-red-500/10 text-red-400" : "bg-blue-500/10 text-blue-400"}`}>
                  {aula.tipo.toUpperCase()}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => moverAula(aula.id, "up")} disabled={idx === 0} className="p-1 text-godoy-lightgray/50 hover:text-godoy-cream transition-colors disabled:opacity-20">
                    <i className="ri-arrow-up-s-line" />
                  </button>
                  <button onClick={() => moverAula(aula.id, "down")} disabled={idx === aulas.length - 1} className="p-1 text-godoy-lightgray/50 hover:text-godoy-cream transition-colors disabled:opacity-20">
                    <i className="ri-arrow-down-s-line" />
                  </button>
                  <a href={aula.arquivo_url} target="_blank" rel="noopener noreferrer" className="p-1 text-godoy-gold/60 hover:text-godoy-gold transition-colors">
                    <i className="ri-external-link-line" />
                  </a>
                  <button onClick={() => handleDeleteAula(aula.id)} className="p-1 text-red-400/60 hover:text-red-400 transition-colors">
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Lista de cursos
  return (
    <div>
      {mensagem && <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md text-emerald-400 text-sm">{mensagem}</div>}
      {erro && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm">{erro}</div>}

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <input type="text" placeholder="Buscar curso..." value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50" />
        <button onClick={openCreate} className="px-5 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm whitespace-nowrap">
          <i className="ri-add-line mr-1" /> Novo Curso
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-godoy-lightgray text-sm py-8 text-center">Nenhum curso encontrado.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-godoy-gray/20 text-godoy-lightgray text-left">
                <th className="pb-3 pr-4 font-medium">Título</th>
                <th className="pb-3 pr-4 font-medium">Categoria</th>
                <th className="pb-3 pr-4 font-medium">Nível</th>
                <th className="pb-3 pr-4 font-medium">Preço</th>
                <th className="pb-3 pr-4 font-medium">Status</th>
                <th className="pb-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-godoy-gray/10 last:border-0">
                  <td className="py-3 pr-4 text-godoy-cream font-medium">{c.titulo}</td>
                  <td className="py-3 pr-4 text-godoy-lightgray text-xs">{c.categoria}</td>
                  <td className="py-3 pr-4 text-godoy-lightgray text-xs">{c.nivel}</td>
                  <td className="py-3 pr-4 text-godoy-cream text-xs">
                    {c.preco != null ? `R$ ${c.preco.toFixed(2).replace(".", ",")}` : "—"}
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c.status === "active" ? "bg-emerald-500/10 text-emerald-400" : c.status === "inactive" ? "bg-red-500/10 text-red-400" : "bg-godoy-gold/10 text-godoy-gold"}`}>
                      {c.status === "active" ? "Ativo" : c.status === "inactive" ? "Inativo" : "Rascunho"}
                    </span>
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openGerenciarAulas(c)} className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-md hover:bg-blue-500/20 transition-all text-xs whitespace-nowrap">
                        <i className="ri-play-list-add-line mr-1" />Aulas
                      </button>
                      <button onClick={() => openEdit(c)} className="px-3 py-1.5 bg-godoy-gold/10 text-godoy-gold rounded-md hover:bg-godoy-gold/20 transition-all text-xs whitespace-nowrap">
                        <i className="ri-edit-line mr-1" />Editar
                      </button>
                      <button onClick={() => handleDelete(c.id)} className="px-3 py-1.5 bg-red-500/10 text-red-400 rounded-md hover:bg-red-500/20 transition-all text-xs whitespace-nowrap">
                        <i className="ri-delete-bin-line mr-1" />Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Curso */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
          <div className="relative bg-godoy-charcoal border border-godoy-gray/30 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h3 className="font-serif text-lg font-semibold text-godoy-gold mb-5">
              {editing ? "Editar Curso" : "Novo Curso"}
            </h3>
            {erro && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm">{erro}</div>}
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm text-godoy-lightgray mb-1.5">Título *</label>
                <input required value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50" />
              </div>
              <div>
                <label className="block text-sm text-godoy-lightgray mb-1.5">Descrição</label>
                <textarea rows={3} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-godoy-lightgray mb-1.5">Categoria</label>
                  <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 appearance-none">
                    {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-godoy-lightgray mb-1.5">Nível</label>
                  <select value={form.nivel} onChange={(e) => setForm({ ...form, nivel: e.target.value })} className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 appearance-none">
                    {NIVEIS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-godoy-lightgray mb-1.5">Preço (R$)</label>
                  <input type="number" step="0.01" min="0" value={form.preco} onChange={(e) => setForm({ ...form, preco: e.target.value })} className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50" />
                </div>
                <div>
                  <label className="block text-sm text-godoy-lightgray mb-1.5">Status</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 appearance-none">
                    <option value="draft">Rascunho</option>
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-godoy-lightgray mb-1.5">URL da Imagem de Capa</label>
                <input type="url" value={form.imagem_url} onChange={(e) => setForm({ ...form, imagem_url: e.target.value })} placeholder="https://..." className="w-full px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60">
                  {saving ? "Salvando..." : editing ? "Salvar Alterações" : "Criar Curso"}
                </button>
                <button type="button" onClick={() => setModalOpen(false)} className="px-5 py-2.5 bg-godoy-gray/30 text-godoy-cream rounded-md hover:bg-godoy-gray/50 transition-all text-sm">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}